// Party Mode's visualization orchestrator - decides WHICH visualization(s)
// are active and WHEN to transition, from real track data (BPM via Deezer,
// energy/danceability/valence via TheAudioDB - see
// src/services/trackFeaturesService.js on the server side). Deliberately
// isolated from THREE.js/DOM/any visualizer's internals: everything here is
// plain data in, plain data out, so it can be unit-tested and reasoned
// about without a browser. The visualizers themselves are never imported or
// modified by this module - see visualizers/adaptive-party.js for the
// THREE.js-side consumer that acts on selectVisualizations()'s decisions.
//
// Library profile fields (tagged once per visualization, on each module's
// own exported PROFILE - see visualizer-registry.js, which replaced the
// hard-coded array this file used to carry directly):
//   energyLevel        0 (calm) .. 1 (intense) - the visual energy the piece carries
//   bpmMin/bpmMax      the visualization was designed to feel right at
//   danceability       0 (never) .. 1 (made for it) - how danceable a track
//                      should be for this piece to be the right answer, matched
//                      against TheAudioDB's danceability
//   movement           0 (organic/loose) .. 1 (geometric/repetitive) - PURELY
//                      DESCRIPTIVE since danceability arrived. It used to be
//                      what danceability was matched against, as a proxy: there
//                      is only one danceability figure in trackData, so leaving
//                      both axes pointed at it would have double-weighted that
//                      one field to 0.30 AND had the two disagree - the library
//                      is full of entries where they are far apart (mainstage
//                      0.50 against 0.95, vinylgroove 0.30 against 0.80), and
//                      averaging a proxy against the real thing is worse than
//                      either alone. Kept as a declared field because it says
//                      something the new axis does not.
//   tonality           0 (cold/dark) .. 1 (warm/vivid) - matched against valence
//   family, enabled    see visualizer-registry.js's own PROFILE shape comment

import { CANDIDATES } from './visualizer-registry.js';
import { FLOAT_TEXTURES_OK } from './float-textures.js';
import { vivierBande, fraicheur, fenetreIntensite } from './diversite.js';

// Only enabled candidates are ever scored/selected - disabled entries
// (particlestorm, mirrorball) still exist in CANDIDATES so ?candidates=1
// can show them (greyed out) rather than have them vanish with no trace.
export const LIBRARY = CANDIDATES.filter(function (c) { return c.enabled; });

// The slice of LIBRARY a given quality tier may run. PROFILE.requiresTier
// (echo is the first: its ping-pong feedback passes need tier >= 1) takes a
// visualizer out of the running entirely on lower tiers - not scored lower,
// absent. null/undefined tier means "no constraint known" and filters
// nothing, which keeps legacy callers (the debug pool painter) unchanged.
// LA BIBLIOTHÈQUE POUR CE MORCEAU-LÀ : ce que le tier autorise, moins ce que
// la porte d'éligibilité refuse. C'est ELLE qui doit alimenter le vivier de
// dansabilité, et pas seulement le calcul de score.
//
// Pourquoi : le vivier est affiché et parcouru à six endroits - la grille VDJ,
// les flèches de la page /radio, le panneau de debug, la console admin - et
// aucun n'appelait scoreCandidates, où vivait la porte. Un visualiseur à porte
// apparaissait donc comme candidat sur tous les morceaux de sa bande de
// dansabilité (mesuré : countdown dans 31% des viviers), y compris ceux où il
// ne pouvait jamais être choisi. Le vivier annonçait un choix qui n'existait
// pas.
export function libraryForTrack(trackData, qualityTier) {
  return libraryForTier(qualityTier).filter(function (v) { return !requirementFailure(v, trackData); });
}

export function libraryForTier(qualityTier) {
  if (qualityTier == null) return LIBRARY;
  return LIBRARY.filter(function (v) {
    return v.requiresTier == null || qualityTier >= v.requiresTier;
  });
}

// Withdraws a visualizer from the rotation at runtime. PROFILE.enabled is read
// once, when this module is first imported, so a visualizer that only finds
// out it cannot run AFTER that - because the assets it needs failed to load -
// has no way to take itself out of the running. gallery.js is the first of
// those: without its photographs it has nothing to display, and being picked
// anyway would put a blank frame on screen for a whole track. Called from
// main.js once the manifest fetch settles.
export function disableVisualization(id) {
  const at = LIBRARY.findIndex(function (c) { return c.id === id; });
  RETIRES.add(id);
  if (at === -1) return false;
  LIBRARY.splice(at, 1);
  // eslint-disable-next-line no-console
  console.warn('[orchestrator] "' + id + '" withdrawn from the rotation - its assets are unavailable');
  return true;
}

// LE CATALOGUE EN BASE, par-dessus les profils du code. Ce que
// l'administration décide sans redéployer : actif ou non, offert au gratuit
// ou non, et les réglages de sélection (dansabilité, énergie, tempo,
// mouvement, tonalité). `lignes` : la table `visualizers` (injectée dans la
// page par le Worker, window.__VISUALISEURS__) ; `options.gratuit` : le
// spectateur n'a droit qu'aux visuels offerts au gratuit. LIBRARY est
// reconstruite EN PLACE — c'est le même tableau que tout le monde tient.
const RETIRES = new Set();
const ETEINTS_SIGNALES = new Set();
let CATALOGUE = null;
let GRATUIT_SEULEMENT = false;
const CHAMPS = [['danceability', 'danceability'], ['energyLevel', 'energy_level'], ['bpmMin', 'bpm_min'], ['bpmMax', 'bpm_max'], ['movement', 'movement'], ['tonality', 'tonality']];
export function appliquerCatalogue(lignes, options) {
  if (Array.isArray(lignes)) { CATALOGUE = new Map(); for (const l of lignes) if (l && l.id) CATALOGUE.set(l.id, l); }
  if (options && typeof options.gratuit === 'boolean') GRATUIT_SEULEMENT = options.gratuit;
  LIBRARY.length = 0;
  for (const c of CANDIDATES) {
    if (c.enabledCode === undefined) c.enabledCode = !!c.enabled;
    const l = CATALOGUE ? CATALOGUE.get(c.id) : null;
    if (l) for (const [k, col] of CHAMPS) if (l[col] != null && isFinite(Number(l[col]))) c[k] = Number(l[col]);
    // Le catalogue peut RALLUMER ce que le code a éteint par décision
    // (particlestorm) — pas ce qu'il a éteint faute de textures flottantes :
    // sans elles, rien d'éteint dans le code ne se rallume.
    const actif = l ? (l.enabled !== false && (c.enabledCode || (l.enabled === true && FLOAT_TEXTURES_OK))) : c.enabledCode;
    // never silently: a visualizer the code offers but the catalogue keeps off is said, with the reason
    if (!actif && c.enabledCode && !ETEINTS_SIGNALES.has(c.id)) {
      ETEINTS_SIGNALES.add(c.id);
      console.warn('[orchestrator] ' + c.id + ' is off: ' + (l && l.enabled === false ? 'public.visualizers.enabled = false (the admin console, or a seed that ran while the code said enabled: false - the seed never lifts it)' : 'disabled in code'));
    }
    c.enabled = actif;
    if (!actif || RETIRES.has(c.id)) continue;
    if (GRATUIT_SEULEMENT && l && l.free === false) continue;
    LIBRARY.push(c);
  }
  return LIBRARY.length;
}
export function estOffertAuGratuit(id) {
  const l = CATALOGUE ? CATALOGUE.get(id) : null;
  return !l || l.free !== false;
}

// CHANTIER 5 - declarative replacement for the old dust/storm-only special
// case: which id PAIRS are allowed to run as two simultaneous layers, the
// effective-energy threshold each needs to clear, and a WEIGHT (0..1) - the
// probability that an eligible pair actually combines rather than staying
// solo, so two compatible visuals don't ALWAYS glue together the instant
// they're both plausible. mainstage+laserarray is in the brief's own list;
// laserarray has no visualizer module (see visualizer-registry.js), so this
// entry can never actually fire - harmless, since layering only happens
// when BOTH ids are present in the (enabled) candidate list.
const COMPATIBLE_PAIRS = [
  { a: 'dust', b: 'storm', threshold: 0.45, weight: 1.0 },
  { a: 'mainstage', b: 'laserarray', threshold: 0.6, weight: 0.5 }
];

// CHANTIER 5 - selection tuning. Named here, together, rather than
// scattered through the function below, since these are the numbers most
// likely to need retuning by ear later.
const RECENCY_PENALTY_LAST_4 = 0.3;
const RECENCY_PENALTY_LAST_8 = 0.6;
const FAMILY_PENALTY = 0.35;
export const HYSTERESIS_FACTOR = 1.15;
const MODE_EXCLUDE_ENERGY_ABOVE = 0.8; // intro/outro/breakdown
const MODE_BONUS_ENERGY_ABOVE = 0.75;  // drop
const MODE_BONUS_MULTIPLIER = 1.4;
// Guard-rail floor (see selectVisualizations' relax-penalties loop) - well
// under a genuine good fit, high enough to only ever trigger when
// penalties/mode-filtering have driven EVERY candidate down at once.
const MIN_VIABLE_SCORE = 0.15;

// CHANTIER 6 (revision) - composition display duration, in TIME (phrases of
// 16 beats), not a flat seconds figure. The old danceability-only
// interpolation almost always landed on a median value (danceability is the
// least reliable of the four TheAudioDB fields - see scoreVisualization's
// own comment on patchy coverage) and never accounted for director.mode at
// all - a breakdown and a drop got the exact same pacing. Base duration per
// mode, in phrases:
const MODE_BASE_PHRASES = {
  intro: 2,
  build: 1,      // moves fast in a rise
  drop: 1,
  sustain: 2,
  breakdown: 3,  // a held breath, not a rush
  outro: 2
};
const BEATS_PER_PHRASE_LOCAL = 16; // matches beat-grid.js's own BEATS_PER_PHRASE - not imported to keep this module dependency-free/pure, see its own header comment
// mix(DANCE_MULT_HIGH, DANCE_MULT_LOW, danceability/100) - only applied when
// danceability is actually present (no invented neutral default - same
// "renormalize on real data only" rule scoreVisualization already follows).
const DANCE_MULT_AT_ZERO = 1.35;
const DANCE_MULT_AT_HUNDRED = 0.75;
const ENERGY_MULT_THRESHOLD = 75;
const ENERGY_MULT = 0.85;
const DURATION_MULT_MIN = 0.6;
const DURATION_MULT_MAX = 1.6;
// Absolute bounds in SECONDS, applied after the beat-based math converts
// through the track's own bpm - whatever the phrase/mode math produces,
// this is the final word. Floor raised from the original 7s: real-world
// observed rotation was averaging ~5s (the 7s floor minus the ~8-beat
// phrase-round compensation below could observably run as short as
// 3-4s at typical bpm) - too fast to register a composition. Raised
// to target a ~10-12s observed average instead.
const DURATION_MIN_S = 15;
const DURATION_MAX_S = 26;
// armSwap() only ever APPLIES on the next phrase downbeat (chantier 6) -
// the desired duration therefore actually lands ~8 beats late on average
// (half of a 16-beat phrase). Subtracted from the desired duration (in
// beats) before converting to the scheduling timer, so the OBSERVED
// duration - desired + that average rounding delay - comes back out to the
// originally desired figure instead of running ~8 beats long every time.
const PHRASE_ROUND_COMPENSATION_BEATS = 8;
// No bpm at all - armSwap() already applies immediately in that case
// (chantier 6), so there's no rounding delay to compensate for; a fixed
// number here would still read as a mechanical, countable rhythm, so this
// is redrawn per composition instead.
const NO_BPM_DURATION_MIN_S = 10;
const NO_BPM_DURATION_MAX_S = 18;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// 0 at the very start/end of a track, 1 through the middle - an
// adjustable-shape "arch" rather than a fixed formula, per the brief's
// requirement that the rise/fall/floor be tunable independently of the
// rest of the orchestration logic. minFactor is a floor, not zero, so
// intro/outro read as "more sober" rather than "off".
export function computeTemporalFactor(progress, opts) {
  const riseEnd = (opts && opts.riseEnd) != null ? opts.riseEnd : 0.1;
  const fallStart = (opts && opts.fallStart) != null ? opts.fallStart : 0.9;
  const minFactor = (opts && opts.minFactor) != null ? opts.minFactor : 0.35;
  const p = clamp01(progress);
  if (riseEnd > 0 && p <= riseEnd) return lerp(minFactor, 1, p / riseEnd);
  if (fallStart < 1 && p >= fallStart) return lerp(1, minFactor, (p - fallStart) / (1 - fallStart));
  return 1;
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

// How long ONE composition stays up before the next re-check even
// considers a swap - see the CHANTIER 6 (revision) constants above for the
// full reasoning. mode: director.mode, nullable (falls back to 'sustain' -
// the middle-of-the-road pacing, not an arbitrary pick, since a decision
// made before director exists yet has no better information to go on).
// rng: injected for deterministic tests, same convention as pickWeighted.
//
// Returns { desiredSeconds, scheduledSeconds }:
//   desiredSeconds    the actual target - what a viewer should perceive
//                      this composition's lifetime as. This is what
//                      "voulue" means for logging/verification purposes.
//   scheduledSeconds  what actually drives the recheck timer - already
//                      compensated for the average phrase-rounding delay,
//                      so the REAL observed lifetime lands back on
//                      desiredSeconds instead of running ~8 beats long.
// LE RYTHME DE CHANGEMENT, réglé par l'utilisateur (console, carte Image).
// Un facteur unique sur la durée voulue de chaque composition, appliqué aux
// deux calculs ci-dessous AVANT leur borne — donc aux extrêmes, c'est la
// borne qui l'emporte, et le réglage ne peut pas produire un défilement
// absurde. 1 = le comportement d'origine ; 2 = deux fois plus long à l'écran.
let FACTEUR_DUREE = 1;
export function reglerRythme(facteur) {
  FACTEUR_DUREE = Number.isFinite(facteur) && facteur > 0 ? facteur : 1;
}

export function computeCompositionDuration(mode, bpm, danceability, energy, rng) {
  const random = rng || Math.random;

  if (!bpm) {
    const s = clamp((NO_BPM_DURATION_MIN_S + random() * (NO_BPM_DURATION_MAX_S - NO_BPM_DURATION_MIN_S)) * FACTEUR_DUREE,
      DURATION_MIN_S, DURATION_MAX_S);
    return { desiredSeconds: s, scheduledSeconds: s };
  }

  const basePhrases = (mode && MODE_BASE_PHRASES[mode] != null) ? MODE_BASE_PHRASES[mode] : MODE_BASE_PHRASES.sustain;
  const baseBeats = basePhrases * BEATS_PER_PHRASE_LOCAL;

  let mult = 1;
  if (danceability != null) mult *= lerp(DANCE_MULT_AT_ZERO, DANCE_MULT_AT_HUNDRED, clamp01(danceability / 100));
  if (energy != null && energy > ENERGY_MULT_THRESHOLD) mult *= ENERGY_MULT;
  mult = clamp(mult, DURATION_MULT_MIN, DURATION_MULT_MAX);

  const secondsPerBeat = 60 / bpm;
  const desiredSeconds = clamp(baseBeats * mult * secondsPerBeat * FACTEUR_DUREE, DURATION_MIN_S, DURATION_MAX_S);
  const scheduledSeconds = Math.max(1, desiredSeconds - PHRASE_ROUND_COMPENSATION_BEATS * secondsPerBeat);
  return { desiredSeconds: desiredSeconds, scheduledSeconds: scheduledSeconds };
}

// radiopi's own /analyze sequencing (marks - see track-analysis.js's
// selectMarks) is the priority for rotation timing whenever it exists and
// is dense enough to drive it. This function is for the two cases where it
// cannot: no analysisTimeline at all for this track, or a real gap between
// two consecutive marks wide enough that a filler transition has to cover
// it (see adaptive-party.js's FILLER_MIN_S). In both, dwell time on the
// current composition is driven by the track's own danceability index
// instead - a calm, non-danceable track is given much more room to sit
// with one visual (up to maxSeconds), while a highly danceable one stays
// close to minSeconds, matching the intuition that a track "made to move
// to" wants more frequent visual change. index: 0 (not danceable at all)
// .. 1 (made for it), same 0..1 scale as resolveDanceabilityIndex/
// profile.danceability - pass null for the neutral midpoint.
// A small +-15% jitter around the danceability-driven target keeps a
// track's own dwell time from reading as a fixed, mechanical cadence
// (same reasoning as computeCompositionDuration's no-bpm branch above),
// clamped back into [minSeconds, maxSeconds] so maxSeconds is a real
// ceiling, never exceeded by the jitter.
const DANCEABILITY_DWELL_JITTER = 0.15;
export function computeDanceabilityDwellSeconds(bpm, index, minSeconds, maxSeconds, rng) {
  const random = rng || Math.random;
  const i = clamp01(index != null ? index : 0.5);
  const base = lerp(maxSeconds, minSeconds, i);
  const jittered = base * (1 - DANCEABILITY_DWELL_JITTER + random() * 2 * DANCEABILITY_DWELL_JITTER);
  const desiredSeconds = clamp(jittered * FACTEUR_DUREE, minSeconds, maxSeconds);

  if (!bpm) return { desiredSeconds: desiredSeconds, scheduledSeconds: desiredSeconds };
  const secondsPerBeat = 60 / bpm;
  const scheduledSeconds = Math.max(1, desiredSeconds - PHRASE_ROUND_COMPENSATION_BEATS * secondsPerBeat);
  return { desiredSeconds: desiredSeconds, scheduledSeconds: scheduledSeconds };
}

function closeness(a, b) {
  return 1 - Math.abs(a - b);
}

// 1 inside [min,max], decaying over `margin` outside it - same soft-edge
// shape as bpmScore below, for a visualizer's own declared PROFILE.prefers
// range (dynamics/percussiveness - see track-analysis.js's computeDirector
// consumer and resonance.js, the first visualizer to use this axis) against
// the track's real measured value.
function rangeScore(min, max, value, margin) {
  if (value >= min && value <= max) return 1;
  const dist = value < min ? min - value : value - max;
  return Math.max(0, 1 - dist / margin);
}
const PREFERS_RANGE_MARGIN = 0.3;
// Every field a visualizer's own PROFILE.prefers may name - see
// scoreVisualization's own loop over this list below. Adding a new prefers
// axis for a future visualizer means adding its name here, not a new
// hand-written block.
// 'lowEnd' is declared by laser-grid.js. No per-track source feeds it yet
// (bands.low is a live per-frame signal, not a track trait), so the axis
// reads as absent and scores neutrally - same situation gridStrength was in
// before hasAnalysis stood in for it. Wiring a real source is one entry in
// adaptive-party.js's trackDataInput when one exists.
const PREFERS_FIELDS = ['dynamics', 'percussiveness', 'density', 'syncopation', 'gridStrength', 'brightness', 'lowEnd'];

// 1 inside the visualization's intended BPM range, decaying over a ~40bpm
// margin outside it rather than a hard cutoff - a track at 108bpm against a
// [60,105] range should still be a plausible (if imperfect) match, not a
// zero.
function bpmScore(bpmMin, bpmMax, bpm) {
  if (bpm == null) return 0.5;
  if (bpm >= bpmMin && bpm <= bpmMax) return 1;
  const dist = bpm < bpmMin ? bpmMin - bpm : bpm - bpmMax;
  return Math.max(0, 1 - dist / 40);
}

// Each of energy/danceability/valence only contributes to the score when
// TheAudioDB actually returned that field - a missing field used to
// default to a fake neutral 0.5 and still get weighted the same as a
// real measurement (eScore alone at 0.35, the single biggest weight).
// TheAudioDB's coverage is genuinely patchy (see trackFeaturesService.js)
// - dust/storm's own energyLevel/movement/tonality all happen to sit
// close to 0.5, so whenever real data was missing they scored well
// against that fake neutral on every axis at once while spotwall/
// neongrid/discosquares/neonparticles (each tuned toward one extreme or
// another) scored comparatively worse - not because they were a worse
// fit, but because the "fit" being measured wasn't real. That
// structural bias is what read as "only NeonPulse/ParticleStorm ever
// plays" for any track TheAudioDB couldn't fully match. bpm (from
// Deezer, required by hasSufficientData - almost always real) anchors
// the score instead; the other axes are added in, with their weight
// redistributed among only the axes actually backed by real data, so a
// bpm-only track is ranked purely on tempo fit - a real signal that
// actually varies across the library - rather than on which entry
// happens to be tuned nearest an arbitrary made-up midpoint.
export function scoreVisualization(viz, trackData) {
  const axes = [];
  // bpm is now omitted when absent, exactly like every other axis here. The
  // paragraph above describes why an invented neutral is corrosive, and then
  // this line spent one - it pushed a flat 0.5 for EVERY candidate at the
  // largest weight in the function whenever bpm was missing. It was defensible
  // while hasSufficientData still REQUIRED bpm (as the comment says it did):
  // the axis was then always real. That requirement was since relaxed to
  // `!!trackData`, and nothing here followed.
  //
  // What it cost: on a track Deezer cannot match - classical, constantly - 0.4
  // of the weight became a constant, diluting the character axes that are the
  // only ones actually saying anything. Scores collapsed toward each other and
  // the weighted-random pick spread almost evenly across the library, so a
  // nocturne got ChromeFlux about as often as Sumi.
  if (trackData.bpm != null) {
    axes.push({ score: bpmScore(viz.bpmMin, viz.bpmMax, trackData.bpm), weight: 0.4 });
  }
  if (trackData.energy != null) {
    axes.push({ score: closeness(viz.energyLevel, trackData.energy / 100), weight: 0.3 });
  }
  // Danceability is deliberately NOT an axis here any more. It now SELECTS the
  // pool instead (see selectDanceabilityPool), and scoring it as well would
  // count it twice: every candidate that survived the pool is already close on
  // this axis, so the axis would contribute almost the same value to all five
  // and its 0.15 would come straight out of the weight the axes that actually
  // still discriminate get to share.
  //
  // Note the axis that was here scored against trackFeatures.danceability -
  // TheAudioDB's 0-100 figure - which is a different quantity from radiopi's
  // index that now drives the pool. Only one of the two should decide this,
  // and the one that is measured off the audio is the better one.
  if (trackData.valence != null) {
    axes.push({ score: closeness(viz.tonality, trackData.valence / 100), weight: 0.15 });
  }
  // PROFILE.prefers (optional) - a visualizer's own stated taste for
  // track character (see track-analysis.js's computeCharacter/director.js's
  // character field), only ever contributing when BOTH the visualizer
  // declares a preference AND the track actually has a measured value for
  // it - resonance.js is the first entry to use this. One generic loop
  // over every recognized field name rather than a hand-written block per
  // field - sumi.js's "density" (no independent signal in
  // the radiopi payload; adaptive-party.js's trackDataInput rides it on
  // the same onset-rate value as percussiveness, under a name that matches
  // how those files reason about it) and clave.js's "gridStrength"
  // (adaptive-party.js maps this straight from director.hasReliableGrid -
  // 1 or 0, no continuous signal exists for it either) are both exactly
  // this same shape, just riding an existing or derived value under their
  // own name. caustics.js's "brightness" is the same shape once more, this
  // time riding artworkPalette.brightness - the only brightness measurement
  // in the payload, and about the COVER rather than the audio, which is the
  // relevant one for the single visualizer in the library whose whole
  // register is luminous.
  if (viz.prefers) {
    for (const field of PREFERS_FIELDS) {
      if (viz.prefers[field] && trackData[field] != null) {
        axes.push({ score: rangeScore(viz.prefers[field][0], viz.prefers[field][1], trackData[field], PREFERS_RANGE_MARGIN), weight: 0.2 });
      }
    }
  }
  // Nothing measurable at all: every candidate ties, and the weighted-random
  // pick spreads across the whole library - "si pas de donnees, mets les
  // toutes". Returning a constant is what makes that happen; dividing by a
  // zero total weight would return NaN and sort the library arbitrarily.
  if (axes.length === 0) return 0.5;
  const totalWeight = axes.reduce(function (sum, a) { return sum + a.weight; }, 0);
  return axes.reduce(function (sum, a) { return sum + a.score * a.weight; }, 0) / totalWeight;
}

// Requiring bpm specifically (as this did before) meant any track Deezer
// couldn't match - not rare, see trackFeaturesService.js - fell back to
// selectVisualizations() never running at all, and the caller
// (adaptive-party.js) delegating the ENTIRE track to a plain
// NeonPulseVisualizer, whose OWN internal rotation only ever cycles
// between 2 of the ~7 library entries (dust/storm) - "sometimes when
// there is no BPM, only the 2 first visualizations rotate". The only
// real requirement is a resolved trackData object to score against at
// all (trackFeaturesService.js's findTrackFeatures always returns one,
// even when every field inside it is null - see its own comment) -
// every individual field below already degrades to a neutral default
// when missing (bpmScore's own null branch, and the energy/dance
// defaults in selectVisualizations), so a trackData with NO real fields
// at all still produces a valid (uniformly-scored, so effectively
// random across the WHOLE library - "si pas de donnees, mets les
// toutes") decision instead of being blocked from orchestrating at all.
// Weighted-random pick among the top N scored candidates - "une part
// d'aleatoire controlee" rather than always picking the single best match,
// so the same track profile doesn't always produce the same visualization.
// topN deliberately defaults to (effectively) the whole library at the
// call site below rather than a small fixed number - a small topN with
// even mildly-uneven scores just reliably re-picks the same handful of
// entries every time ("rotating between 2 is not enough"); scoring
// itself (not a shrunk candidate pool) is what should be doing the work
// of favoring better fits, via each entry's own weight in the draw.
// The draw weight is the score raised to this power, not the score itself.
// The paragraph above already says where the work belongs - "scoring itself
// (not a shrunk candidate pool) [...] via each entry's own weight in the draw"
// - and a LINEAR weight does that job weakly: a candidate scoring 0.45 against
// a best of 1.00 still carries 45% of one of the good ones, and there are
// enough poor fits in a thirty-entry library for them to add up. Measured on a
// classical profile, where the calm entries score 1.00 and the club ones 0.33
// to 0.50: 30% of all picks were still visualizers with a danceability of 0.60
// or more. One nocturne in three got a club look.
//
// Cubing collapses that to single digits without shrinking the pool and
// without ever making any entry impossible - which is the property the comment
// above is protecting, and the reason topN stays at the whole library.
const SELECTION_SHARPNESS = 3;

function drawWeight(score) {
  return Math.pow(Math.max(score, 0.001), SELECTION_SHARPNESS);
}

// LE VIVIER SUIT L'INTENSITÉ DE LA PHASE. Le vivier d'un titre électro va à
// peu près de 0,80 à 0,94 de dansabilité ; les phases les plus fortes du titre
// doivent tomber sur le haut de cette tranche, les plus calmes sur le bas.
// intensity (0..1) est l'énergie de la phase qui commence RELATIVE AU TITRE
// (0 = son passage le plus calme, 1 = le plus fort - voir adaptive-party.js),
// jamais une échelle absolue : sur un titre électro, tout serait « fort ».
//
// On vise la dansabilité min + (max - min) x intensity sur les candidats
// restants, et on tire parmi les INTENSITY_NEIGHBOURS plus proches, pondérés
// par leur score - la variété reste, mais dans le bon registre. Un voisin
// déjà joué sur ce titre pèse INTENSITY_PLAYED_WEIGHT : il départage, il ne
// vide pas la tête du classement.
const INTENSITY_NEIGHBOURS = 4;
const INTENSITY_PLAYED_WEIGHT = 0.35;
export function intensityNeighbours(scored, intensity, playedIds) {
  const placed = scored.filter(function (s) { return s.viz.danceability != null; });
  if (placed.length <= 1) return scored;
  let lo = Infinity, hi = -Infinity;
  for (const s of placed) { lo = Math.min(lo, s.viz.danceability); hi = Math.max(hi, s.viz.danceability); }
  const target = lo + (hi - lo) * intensity;
  const near = placed.slice().sort(function (a, b) {
    const d = Math.abs(a.viz.danceability - target) - Math.abs(b.viz.danceability - target);
    return d !== 0 ? d : b.score - a.score;
  }).slice(0, INTENSITY_NEIGHBOURS);
  return near.map(function (s) {
    return playedIds && playedIds.has(s.id) ? Object.assign({}, s, { score: s.score * INTENSITY_PLAYED_WEIGHT }) : s;
  });
}

// LA BANDE D'INTENSITÉ (01/10/2026). La règle précédente visait min + (max -
// min) x intensité sur ce qui RESTAIT à jouer : le haut du vivier (Pyro,
// Bayfront, Beamwall, seuls au-dessus de 0,90 sur un vivier 0,80-0,94) partait
// sur les premiers passages, et le drop le plus fort retombait sur un 0,80
// (« Get Started » Party : Cascade à 1:20, Echo sur le passage le plus fort).
// Désormais :
//   - la cible se calcule sur TOUT le vivier (lo..hi fixes pour le titre) ;
//   - on prend le plus proche d'elle (les ex æquo se départagent au score) ;
//   - « jamais deux fois tant que le vivier n'est pas passé » vaut DANS la
//     bande : quand tout y a déjà joué, un drop reprend un visuel de son
//     niveau (pas l'un des INTENSITY_RECENT derniers) plutôt que de faire
//     redescendre l'ambiance.
// full : candidats éligibles (sans celui à l'écran) ; played : Set des joués ;
// recent : Set des derniers affichés. Rend la liste à scorer.
export const INTENSITY_RECENT = 2;
// Baisse d'intensité en dessous de laquelle la dansabilité ne redescend pas.
export const INTENSITY_DESCENTE = 0.08;
export function bandeIntensite(full, played, recent, intensity, range, plancher) {
  const placed = full.filter(function (v) { return v.danceability != null; });
  if (placed.length <= 1) return full;
  // Les bornes : celles du vivier ENTIER (range), visuel à l'écran compris —
  // sans lui, retirer Beamwall (0,94) faisait baisser la cible du drop.
  let lo = Infinity, hi = -Infinity;
  for (const v of (range || placed)) if (v.danceability != null) { lo = Math.min(lo, v.danceability); hi = Math.max(hi, v.danceability); }
  const target = lo + (hi - lo) * intensity;
  // `recent` : les derniers affichés, le premier étant celui à l'écran. Un
  // visuel neuf évite les INTENSITY_RECENT derniers ; une reprise n'évite que
  // celui à l'écran (le haut d'un vivier ne compte souvent que trois visuels).
  const recents = recent ? Array.from(recent) : [];
  const dist = function (v) { return Math.abs(v.danceability - target); };
  const etapes = [
    placed.filter(function (v) { return !(played && played.has(v.id)) && recents.indexOf(v.id) < 0; }),
    placed.filter(function (v) { return v.id !== recents[0]; })
  ];
  // PROPORTIONNÉ, PAS TIRÉ AU SORT : le plus proche de la cible, le hasard ne
  // départage que les ex æquo. SANS REDESCENTE sur une montée : `plancher`
  // (la dansabilité à l'écran, voir planIntensite) est une barrière ; il
  // n'est levé que s'il ne laisse plus rien (sommet du vivier atteint).
  for (const avecPlancher of [true, false]) {
    for (const e of etapes) {
      const l = avecPlancher && plancher != null ? e.filter(function (v) { return v.danceability >= plancher - 1e-9; }) : e;
      if (!l.length) continue;
      const d0 = Math.min.apply(null, l.map(dist));
      // La fenêtre (diversite.js) : nulle au drop et à l'ouverture, large au
      // milieu ; dans la fenêtre, le score — fraîcheur comprise — départage.
      return l.filter(function (v) { return dist(v) <= d0 + fenetreIntensite(intensity, hi - lo); });
    }
  }
  return full;
}
const INTENSITY_TIE = 0.005;

// LE PLAN D'INTENSITÉ D'UN TITRE (01/10/2026). Toutes ses phases sont connues
// d'avance (les points de mix), avec leur énergie relative (0..1). On en tire,
// pour chacune, la cible du choix :
//   - les petits creux (moins de INTENSITY_DESCENTE) sont des PALIERS : sur
//     une montée, chaque phase est classée au-dessus de la précédente (la
//     mesure de « Get Started » donne 0,45 / 0,42 / 0,49 pour une première
//     minute qui monte) ;
//   - la cible est le RANG de la phase parmi celles du titre, ramené à 0..1 :
//     proportionnée au titre, et la phase la plus forte reçoit le haut du
//     vivier ;
//   - `monte` : la phase ne redescend pas (plancher dans bandeIntensite).
// phases : [{ atMs, raw }] triées par temps. Rend [{ atMs, raw, lisse, rang, monte }].
export function planIntensite(phases) {
  const out = [];
  let prec = null;
  // LE DROP PRINCIPAL (ph.principal : celui que vise le Countdown, ou celui
  // qu'un titre déclare — cue_points.main_drop_ms) est le SOMMET du titre,
  // quelle que soit la mesure : c'est lui qui reçoit le visuel le plus haut.
  const sommet = Math.max.apply(null, phases.map(function (p) { return p.raw == null ? 0 : p.raw; })) + 0.01;
  for (const ph of phases) {
    const raw = ph.principal ? sommet : (ph.raw == null ? 0 : ph.raw);
    const monte = prec != null && raw >= prec - INTENSITY_DESCENTE;
    const lisse = monte ? Math.max(raw, prec) : raw;
    out.push({ atMs: ph.atMs, raw: raw, lisse: lisse, monte: monte });
    prec = lisse;
  }
  // Rang : par niveau lissé, puis par temps (un palier qui dure monte).
  const ordre = out.map(function (p, k) { return k; }).sort(function (a, b) {
    return out[a].lisse !== out[b].lisse ? out[a].lisse - out[b].lisse : a - b;
  });
  ordre.forEach(function (k, r) { out[k].rang = out.length > 1 ? r / (out.length - 1) : 1; });
  return out;
}

export function pickWeighted(scored, topN, rng) {
  const random = rng || Math.random;
  const sorted = scored.slice().sort(function (a, b) { return b.score - a.score; });
  const pool = sorted.slice(0, Math.min(topN, sorted.length));
  const total = pool.reduce(function (sum, p) { return sum + drawWeight(p.score); }, 0);
  let r = random() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= drawWeight(pool[i].score);
    if (r <= 0) return pool[i];
  }
  return pool[pool.length - 1];
}

// CHANTIER 5 helpers - kept as small pure functions so each rule is
// independently testable (see test-orchestrator.mjs) rather than folded
// inline into selectVisualizations' own body.

// opts.history: [{id, family}, ...] most-recent-first, capped at 8 by the
// caller (adaptive-party.js). Two tiers rather than one smooth falloff -
// "dans les 4 dernieres" / "dans les 8 dernieres" are the brief's own
// exact bands.
export function recencyPenalty(id, history) {
  if (!history || history.length === 0) return 1;
  const idx = history.findIndex(function (h) { return h.id === id; });
  if (idx === -1) return 1;
  if (idx < 4) return RECENCY_PENALTY_LAST_4;
  if (idx < 8) return RECENCY_PENALTY_LAST_8;
  return 1;
}

// Only the IMMEDIATELY previous composition's family matters here (the
// brief: "si la composition precedente appartenait a la meme famille") -
// history[1..] is already covered by recencyPenalty above.
export function familyPenalty(family, history) {
  if (!history || history.length === 0) return 1;
  return history[0].family === family ? FAMILY_PENALTY : 1;
}

// mode: director.mode ('intro'|'build'|'sustain'|'drop'|'breakdown'|
// 'outro'), nullable (callers that don't pass one get no mode gating at
// all - existing/test callers stay unaffected). Returns null to mean
// "excluded entirely" rather than a numeric penalty - a high-energy visual
// during a breakdown isn't a worse fit, it's an actively wrong one.
export function modeAdjustedScore(rawScore, viz, mode) {
  if (mode === 'intro' || mode === 'outro' || mode === 'breakdown') {
    if (viz.energyLevel > MODE_EXCLUDE_ENERGY_ABOVE) return null;
  }
  if (mode === 'drop' && viz.energyLevel > MODE_BONUS_ENERGY_ABOVE) {
    return rawScore * MODE_BONUS_MULTIPLIER;
  }
  return rawScore;
}

// Builds the adjusted-score candidate list for one pass, optionally
// skipping the family and/or recency penalty - see the guard-rail loop in
// selectVisualizations, which relaxes penalties in that exact order
// (family first, then recency) rather than giving up and playing nothing.
// PROFILE.requiresAnalysis - a generic gate, honoured here rather than by the
// visualizer that declares it, so the rule lives where selection happens and
// any future candidate gets it for free.
//
// A visualizer built ON the precomputed timeline has nothing to draw without
// one, and a "degraded" version of it would be a different piece wearing its
// name. No candidate declares it at the moment - the map visualizer that did
// was replaced - but the gate is kept because it is generic and the next
// timeline-dependent piece gets it for free. This is a HARD
// exclusion, not a penalty - a penalised candidate still wins sometimes, which
// is exactly what must never happen here.
function analysisAvailable(trackData) {
  return !!(trackData && trackData.hasAnalysis);
}

// PROFILE.requires - la forme générale de la même idée, pour les conditions
// qu'un seul booléen ne dit pas. Rend `null` si le candidat est éligible, ou
// la RAISON du refus, en clair, pour que l'appelant la journalise.
//
// CETTE PORTE N'EST JAMAIS RELÂCHÉE. Tous les autres filtres de ce fichier -
// excludeId, excludeIds, les bornes d'énergie, le mode - portent une garde
// « seulement si ça ne vide pas le vivier », parce qu'il vaut toujours mieux
// montrer quelque chose d'imparfait que rien. Ici c'est l'inverse, et il faut
// que ce soit écrit quelque part : un visualiseur qui compte à rebours vers un
// drop qu'il ne connaît pas MENT au spectateur. Mieux vaut qu'il n'existe pas.
// Si un jour une garde « ne jamais vider » se met à contourner ceci, c'est un
// bug, pas une amélioration.
//
// Les champs reconnus :
//   analysis: true          le signal radiopi est calculé et reçu
//   beatGrid: true          la grille de temps est mesurée, pas extrapolée
//   drops: n                au moins n drops forts RESTENT devant la position
//   minMsUntilNextDrop: ms  le prochain drop est à au moins ms
//   maxMsUntilNextDrop: ms  ... et au plus ms
//   minBeatsUntilNextDrop / maxBeatsUntilNextDrop: la même chose en TEMPS de
//                           la grille (trackData.beatsUntilNextDrop), pour ce
//                           qui se compte en musique et non en secondes
//
// `gridStrength` reste le 1/0 qu'il est partout ailleurs dans ce fichier : il
// n'existe aucun signal continu de solidité de grille dans la charge utile
// radiopi (voir le commentaire de PREFERS_FIELDS). Le seuil 0.5 demandé par le
// brief revient donc aujourd'hui à « il y a une grille mesurée » - c'est écrit
// ici pour que personne ne croie mesurer autre chose.
const GRID_STRENGTH_MIN = 0.5;

// DEUX SORTES DE CONDITIONS, et les confondre casse le visualiseur qu'elles
// protègent. `analysis` et `beatGrid` portent sur ce QUI EST : elles doivent
// tenir pendant toute la rotation, et adaptive-party.js évince le titulaire dès
// qu'elles cessent. `drops` et `minMsUntilNextDrop` portent sur ce QUI RESTE
// DEVANT : par construction elles cessent de tenir à mesure que le morceau
// avance, et les revérifier en rotation évincerait le visualiseur à l'instant
// précis où il vient de faire son geste. Ce sont des conditions D'ENTRÉE.
//
// `enRotation` sélectionne le jeu. Sans lui - à la sélection - tout est vérifié.
const CONDITIONS_ENTREE = ['drops', 'minMsUntilNextDrop', 'maxMsUntilNextDrop', 'minBeatsUntilNextDrop', 'maxBeatsUntilNextDrop', 'dropTrigger'];
// Un quart de temps de marge sur les bornes en temps : le lancement est calé
// sur un temps, et la position estimée flotte de quelques millisecondes.
const MARGE_TEMPS = 0.25;

export function requirementFailure(viz, trackData, opts) {
  const enRotation = !!(opts && opts.enRotation);
  if (viz.requiresAnalysis && !analysisAvailable(trackData)) return 'analyse absente';
  const req = viz.requires;
  if (!req) return null;
  if (req.analysis && !analysisAvailable(trackData)) return 'analyse absente';
  if (req.beatGrid) {
    const g = trackData ? trackData.gridStrength : null;
    if (g == null) return 'solidite de grille inconnue';
    if (g < GRID_STRENGTH_MIN) return 'grille trop faible (' + g.toFixed(2) + ' < ' + GRID_STRENGTH_MIN + ')';
  }
  if (enRotation) return null;   // au-delà, ce sont des conditions d'entrée
  if (req.drops != null) {
    const n = trackData ? trackData.dropsRemaining : null;
    if (n == null) return 'drops restants inconnus';
    if (n < req.drops) return 'plus assez de drops devant (' + n + ' < ' + req.drops + ')';
  }
  // RÉSERVÉ À L'APPROCHE D'UN GROS DROP (countdown) : jamais dans le tirage
  // ordinaire. adaptive-party.js le lance lui-même dans la fenêtre voulue et
  // passe dropTrigger: true pour ce seul appel - voir _tenterCountdown.
  if (req.dropTrigger && !(trackData && trackData.dropTrigger)) return 'réservé à l’approche d’un gros drop';
  if (req.maxMsUntilNextDrop != null) {
    const ms = trackData ? trackData.msUntilNextDrop : null;
    if (ms == null) return 'aucun drop a venir';
    if (ms > req.maxMsUntilNextDrop) return 'prochain drop trop loin (' + Math.round(ms) + ' ms > ' + req.maxMsUntilNextDrop + ')';
  }
  if (req.minMsUntilNextDrop != null) {
    const ms = trackData ? trackData.msUntilNextDrop : null;
    if (ms == null) return 'aucun drop a venir';
    if (ms < req.minMsUntilNextDrop) {
      return 'prochain drop trop proche (' + Math.round(ms) + ' ms < ' + req.minMsUntilNextDrop + ')';
    }
  }
  if (req.maxBeatsUntilNextDrop != null || req.minBeatsUntilNextDrop != null) {
    const b = trackData ? trackData.beatsUntilNextDrop : null;
    if (b == null) return 'temps avant le drop inconnus';
    if (req.maxBeatsUntilNextDrop != null && b > req.maxBeatsUntilNextDrop + MARGE_TEMPS) {
      return 'prochain drop trop loin (' + b.toFixed(1) + ' temps > ' + req.maxBeatsUntilNextDrop + ')';
    }
    if (req.minBeatsUntilNextDrop != null && b < req.minBeatsUntilNextDrop - MARGE_TEMPS) {
      return 'prochain drop trop proche (' + b.toFixed(1) + ' temps < ' + req.minBeatsUntilNextDrop + ')';
    }
  }
  return null;
}

// `refusals`, quand il est fourni, reçoit une entrée {id, raison} par candidat
// écarté par sa porte d'éligibilité. Un refus silencieux est indiscernable
// d'un visualiseur qui n'existe pas : c'est exactement la panne que ce fichier
// a déjà eue avec l'ancienne LIBRARY codée en dur.
function scoreCandidates(lib, trackData, mode, history, skipFamily, skipRecency, refusals) {
  const out = [];
  for (let i = 0; i < lib.length; i++) {
    const viz = lib[i];
    const raison = requirementFailure(viz, trackData);
    if (raison) { if (refusals) refusals.push({ id: viz.id, raison: raison }); continue; }
    const raw = scoreVisualization(viz, trackData);
    const modeAdjusted = modeAdjustedScore(raw, viz, mode);
    if (modeAdjusted == null) continue; // excluded by mode, not merely penalized
    const fam = skipFamily ? 1 : familyPenalty(viz.family, history);
    const rec = skipRecency ? 1 : recencyPenalty(viz.id, history);
    // LA FRAÎCHEUR (diversite.js) : ce que cet écran a beaucoup montré ces
    // dernières heures pèse moins. Rien n'est banni : tout finit par revenir.
    const fra = fraicheur(viz.id);
    out.push({ id: viz.id, viz: viz, score: modeAdjusted * fam * rec * fra, rawScore: raw });
  }
  return out;
}

// dust+storm (and, if it ever gets a module, mainstage+laserarray) - see
// COMPATIBLE_PAIRS. Only ever combines within the CURRENT candidate pool
// (respects whatever mode-filtering already excluded), and only
// probabilistically past the pair's own energy threshold - not "combines
// whenever technically eligible".
function pickCompatiblePartner(primaryId, poolIds, effectiveEnergy, rng) {
  const random = rng || Math.random;
  for (let i = 0; i < COMPATIBLE_PAIRS.length; i++) {
    const pair = COMPATIBLE_PAIRS[i];
    const partnerId = pair.a === primaryId ? pair.b : (pair.b === primaryId ? pair.a : null);
    if (!partnerId) continue;
    if (poolIds.indexOf(partnerId) === -1) continue;
    if (effectiveEnergy <= pair.threshold) continue;
    if (random() < pair.weight) return partnerId;
  }
  return null;
}

// The pool. Danceability proximity picks WHICH visualizers are candidates for
// this track; everything else in this file then orders them. Kept as a
// separate exported function rather than inlined because the debug panel
// highlights exactly this set (see main.js) and it must be the same
// computation, not a second one that could drift.
//
// index: radiopi's danceability, 0..1 (profile.danceability.value). Null when
// no index has arrived yet - the very first seconds of a track, or a track the
// service could not resolve - and then NOTHING is restricted: the orchestrator
// falls back to considering the whole library, which is what it did before
// this existed.
//
// A PROFILE with no declared danceability cannot be placed on this axis at
// all. Those are sorted after every entry that can, rather than being given an
// invented 0.5 that would park them permanently in the middle of the range.
// Élargi de 7 à 12, puis à 15 (septembre 2026, la bibliothèque passant
// 70 entrées) : sept slots n'en rendaient qu'un cinquième atteignable pour
// un indice donné, et les mêmes visualisations revenaient sur des titres
// pourtant différents. Quinze laissent respirer la rotation sans diluer
// l'appariement, la proximité de dansabilité restant le critère de tri.
export const DANCE_POOL_SIZE = 15;
// La taille effective, réglable à chaud (un clip, un essai) : par défaut la constante.
let TAILLE_VIVIER = DANCE_POOL_SIZE;
export function reglerTailleVivier(n) { TAILLE_VIVIER = Number.isFinite(n) && n > 0 ? Math.round(n) : DANCE_POOL_SIZE; return TAILLE_VIVIER; }

// Ties are broken by a hash of (seed, id), and that detail is what keeps this
// usable rather than being a nicety. The danceability scale is coarse - whole
// tiers of the library share one value exactly - so at 0.3 there are fourteen
// candidates for the pool and the tie-break can decide it outright, not merely
// its ordering. Breaking ties by ID sorts them alphabetically, which is stable
// but also PERMANENT: the same winners every time and the losers never play at
// all, on any track, ever. Measured before this was written - loom, prism,
// resonance, sand, shoji and sumi were unreachable.
//
// Le passage à douze réduit la portée du problème sans le supprimer : dès que
// le nombre d ex aequo dépasse les slots restants, c est encore le hachage qui
// tranche, et c est lui qui fait tourner les égaux d un titre à l autre.
//
// Seeding on the track id gives both properties that are actually wanted: the
// pool is fixed for the whole of one track (re-evaluations mid-track must not
// churn it) and differs between tracks, so equals take turns.
function tieHash(seed, id) {
  let h = 5381;
  const str = String(seed) + '|' + id;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0) / 4294967296;
}

// VERS LE HAUT, JAMAIS VERS LE BAS.
//
// Le vivier prenait les douze plus PROCHES, au-dessus comme en dessous. Sur un
// indice de 0.70, il descendait donc chercher des visualisations à 0.55 aussi
// volontiers qu'il montait à 0.85 - et comme la bibliothèque est plus fournie
// dans le bas de l'échelle, le tirage penchait mécaniquement vers le calme.
// Sur un morceau qui fait danser, c'est l'inverse de ce qu'on veut.
//
// On ne retient donc que les visualisations dont la dansabilité est ÉGALE OU
// SUPÉRIEURE à l'indice, les plus proches d'abord. Le dessous ne sert qu'à
// compléter quand il n'y en a pas douze au-dessus - en haut de l'échelle il
// n'y a plus grand-chose, et un vivier vide serait pire qu'un vivier tiède.
export function selectDanceabilityPool(library, index, size, seed) {
  const lib = library || LIBRARY;
  if (index == null) return lib.slice();
  const want = size || TAILLE_VIVIER;
  if (lib.length <= want) return lib.slice();
  // LA BANDE (15/10/2026, diversite.js) : tous les visuels entre −0,12 et
  // +0,20 de l'indice, tirés au sort (les plus proches favorisés) avec la
  // graine du morceau — stable pendant une écoute, différent d'une écoute à
  // l'autre — plus deux places d'exploration vers le haut. Les « quinze plus
  // proches au-dessus », ci-dessous, donnaient le même vivier à tous les
  // morceaux d'un même indice, et les indices sont presque tous ronds.
  // Gardé pour mémoire et pour comparer (tools/simuler-diversite.mjs).
  return vivierBande(lib, index, want, seed);
  // eslint-disable-next-line no-unreachable
  // Trié par distance à l'indice : pour celles du dessus, la plus proche est
  // aussi la moins dansante de celles qui conviennent, ce qui est bien
  // l'ordre voulu.
  const ordered = lib.slice().sort(function (a, b) {
    const da = a.danceability != null ? Math.abs(a.danceability - index) : Infinity;
    const db = b.danceability != null ? Math.abs(b.danceability - index) : Infinity;
    if (da !== db) return da - db;
    const ha = tieHash(seed, a.id);
    const hb = tieHash(seed, b.id);
    if (ha !== hb) return ha - hb;
    return a.id < b.id ? -1 : (a.id > b.id ? 1 : 0);
  });
  const above = ordered.filter(function (v) {
    return v.danceability != null && v.danceability >= index;
  });
  if (above.length >= want) return above.slice(0, want);
  const below = ordered.filter(function (v) {
    return !(v.danceability != null && v.danceability >= index);
  });
  return above.concat(below).slice(0, want);
}

// trackData: { bpm, energy, danceability, valence, acousticness, mood,
// duration } (all nullable - see trackFeaturesService.js/sonos-visual-
// profile.js). playbackPosition: 0..1. library: defaults to LIBRARY, an
// override is accepted purely so this stays unit-testable without
// depending on the module-level constant.
//
// opts (all optional, all backward-compatible - an absent one just skips
// that rule rather than erroring):
//   envelope  passed straight through to computeTemporalFactor
//   rng       injected RNG for deterministic tests
//   history   [{id, family}, ...] most-recent-first (see recencyPenalty/
//             familyPenalty above)
//   mode      director.mode - see modeAdjustedScore above
//   hysteresisFactor  overrides HYSTERESIS_FACTOR - see its own use below
//   excludeId  hard-excludes one id from the pool (never leaves it empty)
//   minEnergyLevel/maxEnergyLevel  hard energyLevel bounds on the pool
//             (exclusive - viz.energyLevel must be strictly within), same
//             never-leave-nothing guard as excludeId
//
// Returns either { mode: 'fallback' } - trackData itself is null/
// unresolved (see hasSufficientData) - the caller should defer entirely
// to the existing random NeonPulse behavior for this brief window (the
// only case left after hasSufficientData stopped requiring bpm - see
// its own comment), or a full orchestrated decision: { mode:
// 'orchestrated', layers, primary, changed, effectiveEnergy,
// temporalFactor, desiredDurationSeconds, scheduledDurationSeconds,
// scored }. `changed` is false when hysteresis kept the incumbent - the
// caller (adaptive-party.js) uses it to skip re-arming a swap for no
// reason. desiredDurationSeconds/scheduledDurationSeconds - see
// computeCompositionDuration's own comment for what each means.
export function selectVisualizations(trackData, playbackPosition, library, opts) {
  // opts.qualityTier - requiresTier entries (echo) drop out of the running
  // entirely below their floor, before any scoring happens.
  const tierLib = library || libraryForTier(opts && opts.qualityTier);
  const wholeLib = tierLib.length > 0 ? tierLib : LIBRARY;
  // Sans profil du tout (le titre vient de changer, rien n'est encore
  // arrivé) : l'appelant garde son régime de repli.
  if (!trackData) return { mode: 'fallback' };

  // opts.danceability - radiopi's index for this track, 0..1. The FIRST thing
  // applied, before every other filter: it decides who is a candidate at all,
  // and the rest of this function decides the order among them. The filters
  // below (excludeId, energy bounds) each keep their own never-leave-it-empty
  // guard, so un vivier réduit dégrade exactement comme le faisait la
  // bibliothèque entière.
  // LA PORTE D'ÉLIGIBILITÉ EN PREMIER, avant même la dansabilité. Elle était
  // appliquée plus bas, dans scoreCandidates : le vivier RENDU à l'appelant
  // (decision.dancePool, que la console affiche) contenait donc des candidats
  // que le score écartait ensuite en silence.
  const refusals = [];
  const eligibles = wholeLib.filter(function (v) {
    const r = requirementFailure(v, trackData);
    if (r) { refusals.push({ id: v.id, raison: r }); return false; }
    return true;
  });
  // Plus personne ne passe la porte : il n'y a rien à jouer, et c'est la bonne
  // réponse. Surtout pas la garde « ne jamais vider le vivier » qui suit, qui
  // rouvrirait la seule exclusion de ce fichier qui ne se relâche jamais.
  if (eligibles.length === 0) {
    return { mode: 'fallback', refusals: refusals, dancePool: [] };
  }
  const dancePool = selectDanceabilityPool(eligibles, opts && opts.danceability != null ? opts.danceability : null,
    opts && opts.danceabilityPoolSize, opts && opts.danceabilitySeed);
  const fullLib = dancePool.length > 0 ? dancePool : eligibles;

  // opts.excludeId (chantier 6, revision) - a composition that has run
  // WAY past its own desired duration (see adaptive-party.js's own
  // overrun tracking) needs a hard floor under hysteresisFactor's
  // relaxation: with a real track's trackData fixed for its whole
  // duration, the incumbent can keep winning pickWeighted's own draw by
  // sheer score dominance even with hysteresis fully relaxed, since
  // relaxing a PREFERENCE never forces a DIFFERENT outcome. Excluding it
  // outright guarantees the pool actually rotates once critically overrun
  // - never applied if it would leave nothing to pick from.
  const excludeId = opts && opts.excludeId;
  const excludeFiltered = (excludeId && fullLib.some(function (v) { return v.id !== excludeId; }))
    ? fullLib.filter(function (v) { return v.id !== excludeId; })
    : fullLib;

  // opts.excludeIds — LES COMPOSITIONS DÉJÀ JOUÉES SUR CE MORCEAU. La règle :
  // on ne rejoue pas deux fois la même visualisation dans un même titre,
  // sauf si tout le vivier y est déjà passé. Même garde que ci-dessus — si
  // exclure tout ce qui a été joué ne laissait rien, on ignore la liste — et
  // on le dit dans la décision (`poolExhausted`), pour que l'appelant
  // reparte d'un tour neuf.
  const excludeIds = opts && opts.excludeIds && opts.excludeIds.length ? new Set(opts.excludeIds) : null;
  const playedFiltered = excludeIds ? excludeFiltered.filter(function (v) { return !excludeIds.has(v.id); }) : excludeFiltered;
  const poolExhausted = !!(excludeIds && playedFiltered.length === 0);
  // L'INTENSITÉ DE LA PHASE (opts.intensity, 0..1, voir intensityNeighbours)
  // choisit PARMI CE QUI N'A PAS ÉTÉ JOUÉ. Elle gardait les visuels déjà joués
  // candidats, à 0,35 de leur score : sur un titre intense, les mêmes têtes de
  // vivier revenaient tous les trois ou quatre changements (« Fogo », Moulinex,
  // 29/09/2026). La règle ne souffre pas d'exception : jamais deux fois le même
  // dans un titre tant que tout le vivier n'y est pas passé.
  const intensity = opts && opts.intensity != null && isFinite(opts.intensity) ? clamp01(opts.intensity) : null;
  let afterPlayed = poolExhausted ? excludeFiltered : playedFiltered;
  if (intensity != null) {
    const recent = new Set(((opts && opts.history) || []).slice(0, INTENSITY_RECENT).map(function (h) { return h.id; }));
    afterPlayed = bandeIntensite(excludeFiltered, excludeIds, recent, intensity, fullLib, opts && opts.plancherDansabilite);
  }

  // opts.minEnergyLevel/maxEnergyLevel (radiopi mark taxonomy - a strong
  // drop constrains the pool to high-energy families, a fall/breakdown to
  // calm ones - see adaptive-party.js's own mark dispatch). Same
  // never-leave-nothing guard as excludeId above: only applied if it
  // wouldn't empty the pool.
  const minEnergyLevel = opts && opts.minEnergyLevel;
  const maxEnergyLevel = opts && opts.maxEnergyLevel;
  function withinEnergyBounds(v) {
    return (minEnergyLevel == null || v.energyLevel > minEnergyLevel)
      && (maxEnergyLevel == null || v.energyLevel < maxEnergyLevel);
  }
  const energyFiltered = (minEnergyLevel != null || maxEnergyLevel != null) && afterPlayed.some(withinEnergyBounds)
    ? afterPlayed.filter(withinEnergyBounds)
    : afterPlayed;
  const lib = energyFiltered;

  const history = (opts && opts.history) || null;
  const mode = (opts && opts.mode) || null;
  const rng = opts && opts.rng;

  // No artificial envelope without a known duration - per the fallback
  // rule, an unknown duration means "trust the raw energy value", not "make
  // up a shape for a track we can't actually place a position within".
  const temporalFactor = trackData.duration != null
    ? computeTemporalFactor(playbackPosition, opts && opts.envelope)
    : 1;
  // Neutral default (not 0) when energy is missing - defaulting to 0
  // would silently assume "definitely calm" for every data-less track,
  // biasing the compatible-pairs check below (and, before this, the
  // scoring itself) toward the low-energy end for no real reason.
  const energyNorm = trackData.energy != null ? trackData.energy / 100 : 0.5;
  const effectiveEnergy = clamp01(energyNorm * temporalFactor);

  // Guard-rail: relax mode gating, then family, then recency, rather than
  // ever coming back empty - see scoreCandidates' own skipFamily/
  // skipRecency flags. effectiveMode (not the original opts.mode) is what
  // every step below uses once relaxed, so a mode-gate that emptied the
  // pool doesn't silently re-exclude everything on every later step too.
  let effectiveMode = mode;
  let scored = scoreCandidates(lib, trackData, effectiveMode, history, false, false);
  if (scored.length === 0) {
    // The mode gate excluded every enabled candidate outright (only
    // plausible with a near-empty library) - re-score with no mode gating
    // at all rather than produce no decision.
    effectiveMode = null;
    scored = scoreCandidates(lib, trackData, effectiveMode, history, false, false);
  }
  // PLUS PERSONNE, et ce n'est pas le mode qui l'a fait. Les relâchements
  // ci-dessous ne peuvent rien y changer : ils touchent au mode, à la famille
  // et à la récence, jamais à la porte d'éligibilité, qui est la seule
  // exclusion absolue de ce fichier. Il n'y a donc rien à jouer, et le dire est
  // la bonne réponse - pickWeighted sur une liste vide rendait `undefined`, et
  // la ligne suivante lisait `.id` dessus. Cas jamais atteint tant qu'aucun
  // candidat ne déclarait `requires` ; countdown est le premier.
  if (scored.length === 0) {
    return { mode: 'fallback', refusals: refusals, dancePool: fullLib.map(function (v) { return v.id; }) };
  }
  if (Math.max.apply(null, scored.map(function (s) { return s.score; })) < MIN_VIABLE_SCORE) {
    scored = scoreCandidates(lib, trackData, effectiveMode, history, true, false);
  }
  if (Math.max.apply(null, scored.map(function (s) { return s.score; })) < MIN_VIABLE_SCORE) {
    scored = scoreCandidates(lib, trackData, effectiveMode, history, true, true);
  }

  // (L'intensité a déjà choisi la bande des candidats : voir bandeIntensite.)
  let primary = pickWeighted(scored, scored.length, rng);
  let changed = true;

  // Hysteresis - compare against the INCUMBENT's own score under the same
  // mode adjustment but WITHOUT recency/family penalty: staying put isn't
  // a fresh pick, so it shouldn't be punished for "having just played".
  //
  // opts.hysteresisFactor overrides the module default (chantier 6,
  // revision): with a real track's trackData held fixed for its entire
  // duration, the incumbent's own raw score barely moves, and nothing else
  // in this function ever makes hysteresis let go on its own - a
  // composition that has already overrun its OWN target duration (see
  // adaptive-party.js, which computes and passes this) needs the bar
  // lowered progressively, or it can hold effectively forever once
  // established. Defaults to the constant when the caller doesn't pass
  // one (every existing/test call site).
  const hysteresisFactor = (opts && opts.hysteresisFactor != null) ? opts.hysteresisFactor : HYSTERESIS_FACTOR;
  if (history && history.length > 0) {
    const currentId = history[0].id;
    const currentViz = lib.find(function (v) { return v.id === currentId; });
    if (currentViz && primary.id !== currentId) {
      const currentRaw = scoreVisualization(currentViz, trackData);
      const currentAdjusted = modeAdjustedScore(currentRaw, currentViz, mode);
      if (currentAdjusted != null && primary.score < currentAdjusted * hysteresisFactor) {
        primary = { id: currentViz.id, viz: currentViz, score: currentAdjusted, rawScore: currentRaw };
        changed = false;
      }
    } else if (currentViz && primary.id === currentId) {
      changed = false;
    }
  }

  // Hard guard, on top of familyPenalty's own soft x0.35 deterrent: the
  // brief requires a family NEVER plays three times running, not just
  // "rarely" - a penalty alone can still lose that draw if one family
  // dominates the scoring strongly enough. Only relevant when this pick is
  // an actual change (staying on the incumbent doesn't add a new history
  // entry - see adaptive-party.js's _swapTo).
  if (changed && history && history.length >= 2
    && history[0].family === history[1].family
    && primary.viz.family === history[0].family) {
    const withoutFamily = scored.filter(function (s) { return s.viz.family !== primary.viz.family; });
    if (withoutFamily.length > 0) {
      primary = pickWeighted(withoutFamily, withoutFamily.length, rng);
    }
  }

  const poolIds = scored.map(function (s) { return s.id; });
  const danceIds = fullLib.map(function (v) { return v.id; });
  const layers = [primary.id];
  const partnerId = pickCompatiblePartner(primary.id, poolIds, effectiveEnergy, rng);
  if (partnerId) layers.push(partnerId);

  const duration = computeCompositionDuration(mode, trackData.bpm, trackData.danceability, trackData.energy, rng);

  return {
    mode: 'orchestrated',
    layers: layers,
    primary: primary.id,
    changed: changed,
    effectiveEnergy: effectiveEnergy,
    temporalFactor: temporalFactor,
    desiredDurationSeconds: duration.desiredSeconds,
    scheduledDurationSeconds: duration.scheduledSeconds,
    scored: scored,
    // Who danceability let into the running, before ordering. Surfaced for the
    // debug panel and for anyone asking why a given visualizer never appears
    // on a given track.
    dancePool: danceIds,
    // Tout le vivier avait déjà été joué sur ce morceau : la liste des
    // exclusions a été ignorée, un nouveau tour commence.
    poolExhausted: poolExhausted,
    // Qui a été écarté par sa porte d'éligibilité, et pourquoi. Voir
    // requirementFailure : c'est la seule exclusion de ce fichier qui ne se
    // relâche jamais, donc la seule dont l'absence serait inexplicable sans
    // une trace.
    refusals: refusals
  };
}
